import React from 'react';
import useStyles from '../useStyles';
import { CapaServiceType, CapaWithdrawOutput } from '@generated/graphql';
import {
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Tab,
  Tabs
} from '@material-ui/core';
import { clsx } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ConnectChatRooms,
  QuotationChatRooms,
  QuotationPartnerChatRooms
} from './ServiceTypeChatRoom';
import { Check } from '@material-ui/icons';
import { FormValues } from './ConfirmChatContainer';

interface Props {
  matches: boolean;
  data?: CapaWithdrawOutput;
  scopes: CapaServiceType[];
  serviceTab: CapaServiceType;
  isConnect: boolean;
  pagination: {
    offset: number;
    limit: number;
  };
  onClickPage: (value: number) => void;
  setServiceTab: (service: CapaServiceType) => void;
  onSubmit: () => void;
  onChangeRoute: (path: string) => void;
}

const ConfirmChatPresenter = ({
  matches,
  data,
  scopes,
  serviceTab,
  isConnect,
  pagination,
  onClickPage,
  setServiceTab,
  onSubmit,
  onChangeRoute
}: Props) => {
  const classes = useStyles();
  const { control, errors, clearErrors, setValue, watch } = useFormContext<
    FormValues
  >();

  const isValid = watch().consulting && watch().group;

  const makeLabel = (scope: CapaServiceType) => {
    switch (scope) {
      case CapaServiceType.Quotation: {
        return 'CAPA';
      }
      case CapaServiceType.QuotationPartner: {
        return 'CAPA 파트너스';
      }
      case CapaServiceType.Connect: {
        return 'CAPA 커넥트';
      }

      default: {
        return '';
      }
    }
  };

  const count = (scope: CapaServiceType) => {
    return (
      data?.consultingChatRoomsCount.find((v) => v.capaService === scope)
        ?.totalCount ?? 0
    );
  };

  return (
    <div className={classes['Member-Withdrawal-ConfirmChat-root']}>
      <div className="confirm-chat-tabs-root">
        {scopes.length > 1 && (
          <Tabs
            className="service-tab-root"
            value={serviceTab}
            onChange={(_, value: CapaServiceType) => setServiceTab(value)}
          >
            {scopes.map((scope) => (
              <Tab
                value={scope}
                key={scope}
                className={clsx({
                  selected: scope === serviceTab,
                  double: scopes.length === 2,
                  triple: scopes.length === 3
                })}
                label={
                  <>
                    {makeLabel(scope)}
                    <Chip color="secondary" label={count(scope)} />
                  </>
                }
              />
            ))}
          </Tabs>
        )}

        {serviceTab === CapaServiceType.Quotation && (
          <QuotationChatRooms
            data={data}
            serviceTab={serviceTab}
            pagination={pagination}
            onClickPage={onClickPage}
          />
        )}

        {serviceTab === CapaServiceType.QuotationPartner && (
          <QuotationPartnerChatRooms
            data={data}
            serviceTab={serviceTab}
            pagination={pagination}
            onClickPage={onClickPage}
          />
        )}

        {serviceTab === CapaServiceType.Connect && (
          <ConnectChatRooms
            data={data}
            serviceTab={serviceTab}
            pagination={pagination}
            onClickPage={onClickPage}
          />
        )}
      </div>
      <Divider className="divider" />

      <Controller
        control={control}
        name="consulting"
        defaultValue={false}
        rules={{
          required: true
        }}
        render={(renderProps) => (
          <div
            className={clsx('caution-root', {
              error: errors['consulting'] ? true : false
            })}
            onClick={() => {
              clearErrors('consulting');

              setValue('consulting', !renderProps.value);
            }}
          >
            <CustomizedTypography
              className="caution"
              variant="h4"
              color="textPrimary"
              fontWeight="normal"
              gutterBottoms={12}
            >
              탈퇴 완료 시&nbsp;
              <span className="emphasis">진행 중인 모든 상담이 종료 처리</span>
              된다는 사실을 확인했습니다.
            </CustomizedTypography>

            <CustomizedTypography
              className="caution"
              variant="body2"
              color="textSecondary"
            >
              탈퇴 시 캐파, 캐파 파트너스에서 진행하고 있던 모든 상담이 종료
              처리되며, 종료 후 생기는 분쟁은 본인의 책임이니 프로젝트를 끝까지
              마무리하고 탈퇴를 진행해주시기 바랍니다. 추후에 분쟁이 생길 시
              거래 상대방에게 고객님의 연락처를 전달할 수 있고 거래 기록을
              활용할 수 있습니다.
            </CustomizedTypography>

            {matches ? (
              <FormControlLabel
                classes={{
                  root: renderProps.value ? 'checked' : undefined
                }}
                label={
                  renderProps.value ? (
                    <>
                      <Check /> 확인 완료
                    </>
                  ) : (
                    '네, 확인하였습니다.'
                  )
                }
                onChange={(e, checked) => {
                  clearErrors('consulting');
                  setValue('consulting', checked);
                }}
                control={<Checkbox color="primary" />}
              />
            ) : (
              <Checkbox
                color="primary"
                {...renderProps}
                checked={renderProps.value}
                onChange={(_, checked) => {
                  setValue('consulting', checked);
                }}
              />
            )}
          </div>
        )}
      />

      {isConnect && (
        <Controller
          control={control}
          name="group"
          defaultValue={false}
          rules={{
            required: isConnect
          }}
          render={(renderProps) => (
            <div
              className={clsx('caution-root', {
                connect: isConnect,
                error: errors['group'] ? true : false
              })}
              onClick={() => {
                clearErrors('group');

                setValue('group', !renderProps.value);
              }}
            >
              <CustomizedTypography
                variant="h4"
                className="caution"
                color="textPrimary"
                fontWeight="normal"
                gutterBottoms={12}
              >
                탈퇴 완료 시&nbsp;
                <span className="emphasis">고객님이 소유자인 그룹이 삭제</span>
                된다는 사실을 확인했습니다.
              </CustomizedTypography>

              <CustomizedTypography
                className="caution"
                variant="body2"
                color="textSecondary"
              >
                캐파 커넥트의 그룹이 삭제되면 그룹의 모든 참여자가 그룹에 접근할
                수 없습니다. 그룹 삭제로 인한 기록 삭제 등 문제를 유의하여 그룹
                참여자들과 조율한 후 탈퇴를 진행해주세요.
              </CustomizedTypography>

              {matches ? (
                <FormControlLabel
                  classes={{
                    root: renderProps.value ? 'checked' : undefined
                  }}
                  label={
                    renderProps.value ? (
                      <>
                        <Check /> 확인 완료
                      </>
                    ) : (
                      '네, 확인하였습니다.'
                    )
                  }
                  onChange={(e, checked) => {
                    clearErrors('group');
                    setValue('group', checked);
                  }}
                  control={<Checkbox color="primary" />}
                />
              ) : (
                <Checkbox
                  color="primary"
                  {...renderProps}
                  checked={renderProps.value}
                  onChange={(_, checked) => {
                    setValue('group', checked);
                  }}
                />
              )}
            </div>
          )}
        />
      )}

      <div className={clsx(classes['next-stage-cta'], 'confirm-chat')}>
        <CustomizedButton
          fullWidth
          variant="contained"
          color={isValid ? 'primary' : 'default'}
          size="large"
          onClick={onSubmit}
        >
          탈퇴하기
        </CustomizedButton>
        <CustomizedButton
          fullWidth
          variant="outlined"
          color="default"
          size="large"
          onClick={() => onChangeRoute('/mypage')}
        >
          취소
        </CustomizedButton>
      </div>
    </div>
  );
};

export default ConfirmChatPresenter;
